/**
 * StakeholderForm Component
 *
 * Reusable form for creating and editing stakeholders
 */

import { TextField, SelectField, type CreateStakeholderFormData } from '../forms';
import { createStakeholderSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { StakeholderType } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './StakeholderForm.css';

export interface StakeholderFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateStakeholderFormData>;
  /** Submit handler */
  onSubmit: (data: CreateStakeholderFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * Stakeholder create/edit form with validation
 *
 * @example
 * ```tsx
 * <StakeholderForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/stakeholders')}
 * />
 * ```
 */
export function StakeholderForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: StakeholderFormProps): JSX.Element {
  return (
    <EntityForm<CreateStakeholderFormData>
      schema={createStakeholderSchema}
      baseValues={{
        stakeholderType: 'PartnerAgencyOwner',
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Stakeholder"
      formClassName="stakeholder-form"
    >
      {({ register, formState: { errors }, values }) => {
        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Stakeholder Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Jane Doe"
                  helperText="A descriptive name for the stakeholder"
                />

                <SelectField
                  label="Stakeholder Type"
                  {...register('stakeholderType')}
                  value={values.stakeholderType}
                  options={[
                    { value: StakeholderType.PartnerAgencyOwner, label: 'Partner Agency Owner' },
                    { value: StakeholderType.PartnerOwner, label: 'Partner Owner' },
                  ]}
                  error={errors.stakeholderType}
                  required
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the stakeholder"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Organization</h2>

                <TextField
                  label="Organization"
                  {...register('organization')}
                  value={values.organization || ''}
                  error={errors.organization}
                  placeholder="e.g., Partner Agency"
                  helperText="Optional organization the stakeholder belongs to"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Contact Information</h2>

                <TextField
                  label="Contact Email"
                  type="email"
                  {...register('contactEmail')}
                  value={values.contactEmail || ''}
                  error={errors.contactEmail}
                  placeholder="stakeholder@example.com"
                  helperText="Optional stakeholder contact email address"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
